<template>
  <!-- 页面加载过渡动画 -->
  <!-- 增加更长的淡出动画时间，确保样式完全加载后再隐藏 -->
  <Transition
    enter-active-class="transition-all duration-500 ease-out"
    enter-from-class="opacity-0 pointer-events-none"
    enter-to-class="opacity-100 pointer-events-auto"
    leave-active-class="transition-all duration-800 ease-in"
    leave-from-class="opacity-100 pointer-events-auto"
    leave-to-class="opacity-0 pointer-events-none"
  >
    <div
      v-if="loading"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-white backdrop-blur-md"
    >
      <!-- 全屏遮罩加载动画 -->
      <div class="w-full h-full flex items-center justify-center">
        <div class="text-center">
          <!-- 加载动画组合 -->
          <div class="flex justify-center items-center mb-8">
            <!-- 背景光晕效果 -->
            <div
              class="absolute w-20 h-20 bg-gradient-to-r from-blue-400/20 to-purple-500/20 rounded-full blur-xl"
            ></div>
            <!-- 主加载动画 -->
            <div class="relative w-20 h-20">
              <!-- 外圈旋转 -->
              <div
                class="w-full h-full border-4 border-blue-100/50 border-t-blue-600 rounded-full animate-spin"
              ></div>
            </div>
          </div>

          <!-- 加载文字 -->
          <div class="space-y-4">
            <div>
              <p class="text-base text-gray-600 max-w-md">{{ description }}</p>
            </div>

            <!-- 加载提示 -->
            <div v-if="tips.length > 0" class="mt-6">
              <div class="text-sm text-gray-500 font-medium animate-pulse duration-2000">
                {{ currentTip }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

// 组件属性
interface Props {
  loading?: boolean; // 是否显示加载动画
  description?: string; // 加载描述
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  description: "请稍等片刻，正在为您准备精彩内容...",
});

// 响应式数据
const currentTipIndex = ref(0);

// 加载提示语
const tips = [
  "正在优化您的浏览体验...",
  "加载页面资源中...",
  "准备展示精彩内容...",
  "即将完成加载...",
];

// 当前提示语
const currentTip = computed(() => tips[currentTipIndex.value]);

// 动画控制
let tipInterval: NodeJS.Timeout;

const startProgressAnimation = () => {
  // 提示语轮换
  tipInterval = setInterval(() => {
    currentTipIndex.value = (currentTipIndex.value + 1) % tips.length;
  }, 1500);
};

const stopProgressAnimation = () => {
  clearInterval(tipInterval);
};

// 监听loading状态变化
onMounted(() => {
  if (props.loading) {
    startProgressAnimation();
  }
});

// 清理定时器
onUnmounted(() => {
  stopProgressAnimation();
});

// 监听loading状态变化
watch(
  () => props.loading,
  (newVal) => {
    if (newVal) {
      startProgressAnimation();
    } else {
      stopProgressAnimation();
    }
  }
);

// 暴露方法给父组件
defineExpose({
  start: startProgressAnimation,
  stop: stopProgressAnimation,
});
</script>

<style scoped>
/* 反向旋转动画 */
.reverse-spin {
  animation-direction: reverse;
}

/* 确保加载层在最顶层 */
.z-50 {
  z-index: 50;
}
</style>
